import { httpClient } from '@/services/http/httpClient';
import { env } from '@/lib/env';
import { telegramApiMock } from '@/services/api/telegramApi.mock';
import { shareInFlight } from '@/lib/shareInFlight';
import { isApiError } from '@/types';
import type { TelegramLinkCode, TelegramStatus } from '@/types';

/**
 * Telegram account linking (BE Module 2).
 *
 * All three routes are session-authenticated but exempt from the onboarding and
 * active-plan guards — linking is offered before onboarding completes, so the
 * normal guard cascade would 403 it.
 */
export const telegramApiReal = {
  /**
   * POST /api/telegram/link-code
   *
   * Mints a short-lived code and the deep link that carries it. The caller
   * opens `tMeUrl`; the user presses Start; the bot receives `/start <code>`
   * with the payload invisible to them.
   */
  async createLinkCode(): Promise<TelegramLinkCode> {
    return httpClient.post<TelegramLinkCode>('/telegram/link-code');
  },

  /** GET /api/telegram/status */
  async getStatus(): Promise<TelegramStatus> {
    return httpClient.get<TelegramStatus>('/telegram/status');
  },

  /** DELETE /api/telegram/link — flips the identity to UNLINKED. */
  async unlink(): Promise<void> {
    await httpClient.delete('/telegram/link');
  },
};

export type TelegramApi = typeof telegramApiReal;

/**
 * The two ways the backend can be unable to serve this feature rather than
 * failing at it:
 *
 * - **404** — the route is not deployed. Was the whole story while Module 2 was
 *   unmerged; it is now on `main`, so this branch is mostly historical.
 * - **503 `TELEGRAM_NOT_CONFIGURED`** — the route is deployed but the
 *   environment has no `TELEGRAM_BOT_USERNAME`, so the backend refuses to hand
 *   back a `t.me/undefined` link. This is what staging and production currently
 *   answer, because the deploy wires no `TELEGRAM_*` secret.
 *
 * Deliberately narrow, for the same reason as the billing equivalent: 401/403
 * (auth), a *bare* 5xx (outage) and network errors must surface, not be masked
 * by a fixture. A 503 only qualifies when it carries that specific code — a
 * load balancer shedding load says 503 too, and hiding it behind a fixture
 * would turn an outage into a working-looking screen.
 */
export function isEndpointUnavailable(err: unknown): boolean {
  if (isApiError(err)) {
    if (err.error.code === 'NOT_FOUND') return true;
    if (err.error.code === 'TELEGRAM_NOT_CONFIGURED') return true;
  }
  const status = (err as { response?: { status?: number }; status?: number } | null | undefined)
    ?.response?.status;
  return status === 404;
}

/**
 * Serve the fixture ONLY when the backend says it cannot offer the feature at
 * all. Any other error propagates unchanged, and the moment an environment is
 * actually configured the real payload flows through with no edit here.
 */
function withMockFallback<T>(
  real: () => Promise<T>,
  mock: () => Promise<T>,
  route: string
): () => Promise<T> {
  return async () => {
    try {
      return await real();
    } catch (err) {
      if (isEndpointUnavailable(err)) {
        console.warn(
          `[telegram] ${route} unavailable (route not deployed, or no ` +
            `TELEGRAM_BOT_USERNAME on this environment) — serving mock fixture ` +
            `(dev only). Auto-switches to the BE once it can answer.`
        );
        return mock();
      }
      throw err;
    }
  };
}

function resolveTelegramApi(): TelegramApi {
  // SECURITY BOUNDARY: every mock path lives inside this `import.meta.env.DEV`
  // block, so a production/staging build eliminates the fixtures entirely. A
  // fabricated `linked: true` in a deployed build would tell a user their
  // account is connected when no identity exists on the backend — they would
  // then wait for notifications that can never arrive.
  if (import.meta.env.DEV) {
    if (env.ENABLE_MOCK_API) return telegramApiMock;

    return {
      ...telegramApiReal,
      createLinkCode: withMockFallback(
        telegramApiReal.createLinkCode,
        telegramApiMock.createLinkCode,
        'POST /telegram/link-code'
      ),
      getStatus: withMockFallback(
        telegramApiReal.getStatus,
        telegramApiMock.getStatus,
        'GET /telegram/status'
      ),
      unlink: withMockFallback(
        telegramApiReal.unlink,
        telegramApiMock.unlink,
        'DELETE /telegram/link'
      ),
    };
  }

  return telegramApiReal;
}

const resolved = resolveTelegramApi();

export const telegramApi: TelegramApi = {
  ...resolved,
  // Three callers want the status at the same moment on a dashboard load: the
  // setup checklist's signal fetcher, each mounted card's mount refresh, and
  // the link poll. One answer, one request.
  getStatus: shareInFlight(resolved.getStatus),
};
